// Qué promos de venta conserva o pierde cada forma de pago, a partir de lo apartado en la sesión.
import { anchorLineOf } from '@/domains/sales/wizard/buildSale';
import type { ReservedLine, WizardSession } from '@/domains/sales/wizard/types';
import { lineDiscounts } from './applySelection';
import { heldCodeOf, type SureKeepReleased } from './sureKeepRelease';
import type { PromotionPaymentOption } from '@/domains/retail/admin/promotions/api/promotionsContract';

export type PaymentTrack = 'SUREKEEP' | 'CASH' | 'EXTERNAL';

/** Una promo de venta en un producto, con lo que baja o lo que entrega. */
export interface PaymentPromotion {
    promotionId: string;
    name: string;
    /** Línea donde va; vacío si la venta no tiene teléfono. */
    lineId: string;
    productName: string;
    amountCents: number;
    /** Lo que entrega cuando no baja el precio (bono, cupón…). */
    text: string;
    sureKeepOnly: boolean;
    /** Formas de pago que admite; `null` = cualquiera, ausente = decide `sureKeepOnly`. */
    paymentOptions?: readonly PromotionPaymentOption[] | null;
    /** Se soltó al pasar a contado: con SureKeep se recupera. */
    recovered?: boolean;
}

/** Lo que se sabe de una promo por su id. */
export interface PromotionFacts {
    name: string;
    sureKeepOnly: boolean;
    text: string;
    paymentOptions?: readonly PromotionPaymentOption[] | null;
    /** Categorías de su alcance; `null` = cualquiera. */
    categories?: readonly string[] | null;
}

export interface SalePromotionsOptions {
    facts: (promotionId: string) => PromotionFacts;
    productName: (line: ReservedLine) => string;
}

/** Por nombre y luego por producto. */
export const byName = (a: PaymentPromotion, b: PaymentPromotion): number =>
    a.name.localeCompare(b.name, 'es-MX') || a.productName.localeCompare(b.productName, 'es-MX');

/** Promos apartadas por producto: las que bajan una línea y, sin línea, las de la venta en el teléfono. */
export function salePromotionsOf(
    session: WizardSession,
    { facts, productName }: SalePromotionsOptions,
): PaymentPromotion[] {
    const lines = (session.reservedLines ?? []).filter(
        (l) => l.status !== 'RELEASING' && l.role !== 'GIFT',
    );
    const out: PaymentPromotion[] = [];
    for (const line of lines) {
        for (const d of lineDiscounts(line)) {
            if (d.discountCents <= 0) continue;
            const { categories: _categories, ...known } = facts(d.promotionId);
            out.push({
                ...known,
                promotionId: d.promotionId,
                lineId: line.lineId,
                productName: productName(line),
                amountCents: d.discountCents,
            });
        }
    }
    const placed = new Set(out.map((p) => p.promotionId));
    const anchor = anchorLineOf(session);
    const refs = session.salePromotions ?? (session.salePromotion ? [session.salePromotion] : []);
    for (const ref of refs) {
        if (placed.has(ref.promotionId)) continue;
        placed.add(ref.promotionId);
        const { categories, ...known } = facts(ref.promotionId);
        // Una de accesorios va con el primer accesorio de su alcance; las demás, con el teléfono.
        const reachesAnchor =
            !categories ||
            !anchor?.product?.category ||
            categories.includes(anchor.product.category);
        const target = reachesAnchor
            ? anchor
            : (lines.find((l) => l.product?.category && categories.includes(l.product.category)) ??
              anchor);
        out.push({
            ...known,
            promotionId: ref.promotionId,
            lineId: target?.lineId ?? '',
            productName: target ? productName(target) : '',
            amountCents: 0,
        });
    }
    return out.sort(byName);
}

/** Formas de pago del catálogo que cubre cada tarjeta del paso Pago. */
const TRACK_OPTIONS: Record<PaymentTrack, readonly PromotionPaymentOption[]> = {
    SUREKEEP: ['SUREKEEP'],
    CASH: ['CASH', 'CARD', 'WIRE'],
    EXTERNAL: ['PAYJOY', 'CREDIYA', 'LESPAGO', 'INNOVA'],
};

/** La promo aplica con esa forma de pago; sin `paymentOptions` solo cuenta si es solo SureKeep. */
export function admitsTrack(promo: PaymentPromotion, track: PaymentTrack): boolean {
    const options = promo.paymentOptions;
    if (options === undefined) return track === 'SUREKEEP' || !promo.sureKeepOnly;
    return !options?.length || options.some((o) => TRACK_OPTIONS[track].includes(o));
}

/** Conserva lo que esa forma de pago admite; pierde lo que se recupera con SureKeep. */
export function splitForTrack(
    promos: readonly PaymentPromotion[],
    track: PaymentTrack,
): { kept: PaymentPromotion[]; lost: PaymentPromotion[] } {
    return {
        kept: promos.filter((p) => admitsTrack(p, track)),
        lost:
            track === 'SUREKEEP'
                ? []
                : promos.filter((p) => !admitsTrack(p, track) && admitsTrack(p, 'SUREKEEP')),
    };
}

/** Una por promo, aunque baje varios productos. */
export function uniqueByPromotion(promos: readonly PaymentPromotion[]): PaymentPromotion[] {
    const seen = new Set<string>();
    return promos.filter((p) => {
        if (seen.has(p.promotionId)) return false;
        seen.add(p.promotionId);
        return true;
    });
}

/** Lo que dejan de bajar las promos perdidas. */
export function lostCents(promos: readonly PaymentPromotion[]): number {
    return promos.reduce((sum, p) => sum + p.amountCents, 0);
}

const codeKey = (code: string): string => `code:${code}`;

/**
 * El código solo aplica con SureKeep salvo que el back lo haya dejado sin SureKeep: siguió de contado,
 * quedó apartado con contado o derivada guardada.
 */
export function heldCodeIsSureKeepOnly(
    session: WizardSession,
    code: string,
    anyFinancierCodes: readonly string[],
): boolean {
    if (anyFinancierCodes.includes(code)) return false;
    if (heldCodeOf(session)?.code !== code) return true;
    const kind = session.paymentMethodKind;
    return !(kind === 'CASH_OR_CARD' || kind === 'EXTERNAL');
}

/** El código apartado en la venta como una promo más, con lo que baja. */
export function heldCodePromotionOf(
    session: WizardSession,
    {
        sureKeepOnly,
        productName,
        evaluatedCents,
    }: {
        sureKeepOnly: (code: string) => boolean;
        /** Lo que baja según la evaluación; se usa si la línea no trae `codeDiscountCents`. */
        evaluatedCents?: (code: string) => number | undefined;
    } & Pick<SalePromotionsOptions, 'productName'>,
): PaymentPromotion | null {
    const held = heldCodeOf(session);
    if (!held) return null;
    const amountCents =
        held.amountCents > 0 ? held.amountCents : (evaluatedCents?.(held.code) ?? 0);
    const line = (session.reservedLines ?? []).find((l) => l.lineId === held.lineId);
    return {
        promotionId: codeKey(held.code),
        name: `Código ${held.code}`,
        lineId: held.lineId,
        productName: line ? productName(line) : '',
        amountCents,
        text: '',
        sureKeepOnly: sureKeepOnly(held.code),
    };
}

/** Lo solo SureKeep que se soltó de contado y la venta ya no tiene apartado. */
export function releasedPromotionsOf(
    session: WizardSession,
    released: SureKeepReleased,
    held: readonly PaymentPromotion[],
    { facts, productName }: SalePromotionsOptions,
): PaymentPromotion[] {
    const heldIds = new Set(held.map((p) => p.promotionId));
    const anchor = anchorLineOf(session);
    const lineOf = (lineId: string, onAnchor: boolean): ReservedLine | undefined =>
        onAnchor ? anchor : (session.reservedLines ?? []).find((l) => l.lineId === lineId);
    const out: PaymentPromotion[] = [];
    for (const r of released.choices) {
        if (heldIds.has(r.choice.promotionId)) continue;
        const line = lineOf(r.lineId, r.onAnchor);
        if (!line) continue;
        const { categories: _categories, ...known } = facts(r.choice.promotionId);
        out.push({
            ...known,
            promotionId: r.choice.promotionId,
            lineId: line.lineId,
            productName: productName(line),
            amountCents: r.amountCents,
            sureKeepOnly: true,
            recovered: true,
        });
    }
    const code = released.code;
    if (code && !heldIds.has(codeKey(code.code))) {
        const line = lineOf(code.lineId, code.lineId === '' || code.lineId === anchor?.lineId);
        out.push({
            promotionId: codeKey(code.code),
            name: `Código ${code.code}`,
            lineId: line?.lineId ?? '',
            productName: line ? productName(line) : '',
            amountCents: code.amountCents,
            text: '',
            sureKeepOnly: true,
            recovered: true,
        });
    }
    return out.sort(byName);
}

/**
 * Total de cada forma de pago: SureKeep recupera lo soltado y cada forma regresa lo apartado que no
 * admite, sin pasar en cada línea de lo que la línea tiene de descuento (`lineDiscountCents`).
 */
export function trackTotalCents(
    saleTotalCents: number,
    promos: readonly PaymentPromotion[],
    track: PaymentTrack,
    lineDiscountCents: (lineId: string) => number | undefined = () => undefined,
): number {
    const recovered = track === 'SUREKEEP' ? lostCents(promos.filter((p) => p.recovered)) : 0;
    const byLine = new Map<string, number>();
    for (const p of promos.filter((x) => !x.recovered && !admitsTrack(x, track))) {
        byLine.set(p.lineId, (byLine.get(p.lineId) ?? 0) + p.amountCents);
    }
    let notAdmitted = 0;
    for (const [lineId, cents] of byLine) {
        notAdmitted += Math.min(cents, lineDiscountCents(lineId) ?? cents);
    }
    return Math.max(0, saleTotalCents - recovered) + notAdmitted;
}
